import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const handleGoogleSignIn = () => {
  const redirectUrl = window.location.origin + '/';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('welcomeBack') + '!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9] px-4 relative">
      {/* Language Switcher - fixed z-index for mobile */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-1 bg-[#F5F5F4] rounded-full p-1 shadow-sm">
        <button
          type="button"
          className={`rounded-full h-8 px-3 text-xs font-medium transition-colors ${language === 'en' ? 'bg-[#57534E] text-white' : 'text-[#78716C] hover:bg-white/50'}`}
          onClick={() => setLanguage('en')}
          data-testid="lang-en-btn"
        >
          EN
        </button>
        <button
          type="button"
          className={`rounded-full h-8 px-3 text-xs font-medium transition-colors ${language === 'nl' ? 'bg-[#57534E] text-white' : 'text-[#78716C] hover:bg-white/50'}`}
          onClick={() => setLanguage('nl')}
          data-testid="lang-nl-btn"
        >
          NL
        </button>
      </div>

      <div className="w-full max-w-md animate-slide-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/logo.png" alt="Organizer Space" className="h-20 w-20 object-contain" />
          </div>
          <h1 className="text-3xl font-medium text-[#292524]">{t('appName')}</h1>
          <p className="text-[#78716C] mt-2">{t('tagline')}</p>
        </div>
        
        <Card className="border-0 shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-medium">{t('welcomeBack')}</CardTitle>
            <CardDescription>{t('enterCredentials')}</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Google Sign-In */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 rounded-full mb-4 flex items-center justify-center gap-3 border-[#D6D3D1] hover:bg-[#F5F5F4] text-[#44403C]"
              onClick={handleGoogleSignIn}
              data-testid="google-signin-btn"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              {t('signInWithGoogle')}
            </Button>

            {/* Divider */}
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E7E5E4]" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-[#A8A29E]">{t('orContinueWith')}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={t('password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11"
                  data-testid="login-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 rounded-full bg-[#57534E] hover:bg-[#57534E]/90"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('signingIn')}
                  </>
                ) : (
                  t('signIn')
                )}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-[#78716C]">
              {t('dontHaveAccount')}{' '}
              <Link to="/register" className="text-[#78866B] hover:underline font-medium" data-testid="register-link">
                {t('createOne')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
